import React, { type ComponentPropsWithoutRef, useMemo, useCallback } from 'react';
import { ErrorFlag } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types'; // ignore-for-ENGHEALTH-17759
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useFlagAnalytics } from '../../../../common/ui/use-flag-analytics/index.tsx';
import {
	useFormSubmitIssueIds,
	useFormSubmitErrorData,
} from '../../../../controllers/use-form-submit-state/main.tsx';
import { TEST_ID, FLAG_ID, BULK_EDIT_ERROR_LEARN_MODE_ARTICLE_ID } from './constants.tsx';
import messages from './messages.tsx';

type Props = {
	onDismissed: () => void;
} & FlagCustomRenderProps;

type FlagActions = ComponentPropsWithoutRef<typeof ErrorFlag>['actions'];

export const OperationPartialErrorFlag = ({ onDismissed, ...props }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const issueIds = useFormSubmitIssueIds();
	const errorData = useFormSubmitErrorData();
	const openInProductHelp = useHelpPanelLink();

	const analyticsAttributes = useMemo(
		() => ({
			numSelectedCards: issueIds.length ?? 0,
			numFailedCards: errorData?.failedCount ?? 0,
			failedIssueKeys: errorData?.issueKeys ?? [],
			flagType: 'error',
		}),
		[errorData?.failedCount, errorData?.issueKeys, issueIds.length],
	);

	useFlagAnalytics(analyticsAttributes);

	const getViewSummaryURL = useMemo(() => {
		const errorDataIssueIds = errorData?.issueKeys?.join(',');
		const jqlSearch = encodeURIComponent(`issueKey in (${errorDataIssueIds})`);
		return `/issues/?jql=${jqlSearch}`;
	}, [errorData?.issueKeys]);

	const handleLearnMoreClick = useCallback(() => {
		openInProductHelp(BULK_EDIT_ERROR_LEARN_MODE_ARTICLE_ID);
	}, [openInProductHelp]);

	const flagActions: FlagActions = useMemo(
		() => [
			{
				content: formatMessage(messages.viewSummary),
				href: getViewSummaryURL,
				target: '_blank',
			},
			{
				content: formatMessage(messages.learnMore),
				onClick: handleLearnMoreClick,
			},
		],
		[formatMessage, getViewSummaryURL, handleLearnMoreClick],
	);

	return (
		<ErrorFlag
			{...props}
			testId={TEST_ID}
			id={FLAG_ID}
			title={formatMessage(messages.title, {
				failedCount: errorData?.failedCount,
				totalCount: errorData?.totalCount,
			})}
			description={formatMessage(messages.description)}
			onDismissed={onDismissed}
			messageId="bulk-operations-common.ui.submit-status.flags.partial-error-flag.error-flag"
			messageType="transactional"
			actions={flagActions}
		/>
	);
};
